import { API_BASE_URL } from "src/config/apiBase";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    ShoppingCart,
    Package,
    ArrowRight,
    Bell,
    IndianRupee,
    Boxes,
} from "lucide-react";
import axios from "axios";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { isInFilter } from "../utils/dateFilter";

const url = API_BASE_URL;

function flattenLeads(groupedLeads) {
    if (!Array.isArray(groupedLeads)) return [];
    return groupedLeads.flatMap((group) => (Array.isArray(group?.leads) ? group.leads : [group]));
}

function getProductStockInfo(product) {
    const variations = Array.isArray(product?.variations) ? product.variations : [];
    if (variations.length > 0) {
        return variations.map((v) => ({
            stock: v?.stock ?? v?.totalStock ?? product?.totalStock,
            status: v?.stockStatus ?? product?.stock,
        }));
    }
    return [{ stock: product?.totalStock ?? product?.stock, status: product?.stock }];
}

function isOutOfStock(entry) {
    const status = String(entry?.status || "").toLowerCase();
    if (status.includes("out of stock")) return true;
    const qty = Number(entry?.stock);
    return Number.isFinite(qty) && qty <= 0;
}

function isLowStock(entry) {
    const status = String(entry?.status || "").toLowerCase();
    if (status.includes("unlimited")) return false;
  if (status.includes("out of stock")) return false;
    const qty = Number(entry?.stock);
    return Number.isFinite(qty) && qty > 0 && qty < 10;
}

function formatCurrency(value) {
    const amount = Number(value) || 0;
    return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const SummaryMetric = ({ label, value, hint, tone = "default" }) => {
    const toneClass =
        tone === "success"
            ? "text-lime-300"
            : tone === "warn"
                ? "text-amber-300"
                : tone === "danger"
                    ? "text-rose-300"
                    : "text-ink";

    return (
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 min-w-[120px]">
            <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">{label}</p>
            <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
            {hint ? <p className="text-[11px] text-ink-muted mt-0.5">{hint}</p> : null}
        </div>
    );
};

const SummaryColumn = ({ title, icon: Icon, tone, children, onViewMore, viewLabel, scrollTheme = "crm" }) => (
    <div className={`rounded-xl border p-3 sm:p-4 bg-gradient-to-br ${tone}`}>
        <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-ink shadow-sm">
                    <Icon size={16} />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-ink">{title}</h3>
            </div>
            <button
                type="button"
                onClick={onViewMore}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-cyan hover:text-cyan-300"
            >
                {viewLabel}
                <ArrowRight size={12} />
            </button>
        </div>
        <div className={`flex gap-2 dash-metric-scroll dash-metric-scroll--${scrollTheme}`}>{children}</div>
    </div>
);

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

function getFilterLabel(filter) {
    if (typeof filter === "string") return filter;
    if (filter?.type === "Custom Date" && filter.from && filter.to) {
        return `${filter.from} to ${filter.to}`;
    }
    return "Selected period";
}

const SummaryDateFilter = ({ value, onChange }) => {
    const [activeTab, setActiveTab] = useState(
        typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "Today"
    );
    const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
    const [tempRange, setTempRange] = useState({
        from: value?.from || "",
        to: value?.to || "",
    });

    const handleTabClick = (tab) => {
        if (tab === "Custom Date") {
            setActiveTab("Custom Date");
            setShowCustom(true);
            return;
        }
        setActiveTab(tab);
        setShowCustom(false);
        onChange(tab);
    };

    const handleApplyCustom = () => {
        if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
        onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
        setShowCustom(false);
    };

    return (
        <div className="w-full sm:w-auto sm:min-w-[280px]">
            <div className="flex items-center gap-1 dash-filter-scroll rounded-xl border border-line bg-surface-2 p-1">
                {FILTER_TABS.map((tab) => {
                    const isActive = activeTab === tab;
                    return (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => handleTabClick(tab)}
                            className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap ${
                                isActive
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-ink-muted hover:bg-white/10 hover:text-ink"
                            }`}
                        >
                            {tab}
                        </button>
                    );
                })}
            </div>

            {showCustom && (
                <div className="mt-2 rounded-xl border border-dashed border-line-strong bg-surface-2 p-2.5">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
                            From
                            <input
                                type="date"
                                value={tempRange.from}
                                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
                            To
                            <input
                                type="date"
                                value={tempRange.to}
                                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
                            />
                        </label>
                    </div>
                    <div className="mt-2 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setShowCustom(false);
                                if (typeof value === "string") setActiveTab(value);
                            }}
                            className="rounded-lg border border-line px-3 py-1 text-xs font-semibold text-ink-secondary hover:bg-white/10"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApplyCustom}
                            disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
                            className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function DailyBusinessSummary() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("Today");
    const [loading, setLoading] = useState(true);
    const [leads, setLeads] = useState([]);
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);

    const token = decryptToken("accessToken") || "";
    const user = getDecryptedUserFromCookie("gta_mainstateuser");

    const fetchSummaryData = useCallback(async () => {
        if (!user?.main_id || !token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const [leadsRes, ordersRes, productsRes] = await Promise.all([
                fetch(`${url}/leads/getall`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                fetch(`${url}/payment/adminorder`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${url}/grouped-product/search-all-products/${user.main_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { includeInactive: true },
                }),
            ]);

            const leadsJson = await leadsRes.json();
            const ordersJson = await ordersRes.json();

            setLeads(flattenLeads(leadsJson));
            setOrders(Array.isArray(ordersJson) ? ordersJson : []);
            setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
        } catch (error) {
            console.error("Daily summary fetch failed:", error);
        } finally {
            setLoading(false);
        }
    }, [token, user?.main_id]);

    useEffect(() => {
        void fetchSummaryData();
    }, [fetchSummaryData]);

    const summary = useMemo(() => {
        const activeLeads = leads.filter((lead) => !lead.archieve && lead.archieve !== "true");
        const periodLeads = activeLeads.filter((lead) => isInFilter(lead.createdAt, filter));

        const todayStr = new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
        const remindersDueToday = activeLeads.filter((lead) => {
            if (!lead.remainder_date) return false;
            const reminderDate = new Date(lead.remainder_date).toLocaleDateString("en-IN", {
                timeZone: "Asia/Kolkata",
            });
            return reminderDate === todayStr;
        }).length;

        const periodOrders = orders.filter((order) =>
            isInFilter(order.createdAt || order.updatedAt, filter)
        );

        const deliveredOrders = periodOrders.filter(
            (order) => String(order.orderStatus || "").toLowerCase() === "delivered"
        );
        const pendingOrders = periodOrders.filter((order) => {
            const status = String(order.orderStatus || "").toLowerCase();
            return status && status !== "delivered" && status !== "cancelled";
        });
        const cancelledOrders = periodOrders.filter(
            (order) => String(order.orderStatus || "").toLowerCase() === "cancelled"
        );
        const periodRevenue = deliveredOrders.reduce(
            (sum, order) => sum + (Number(order.amountafterdiscount) || 0),
            0
        );

        const periodProducts = products.filter((product) => isInFilter(product.createdAt, filter));
        let outOfStock = 0;
        let lowStock = 0;

        products.forEach((product) => {
            getProductStockInfo(product).forEach((entry) => {
                if (isOutOfStock(entry)) outOfStock += 1;
                else if (isLowStock(entry)) lowStock += 1;
            });
        });

        const filterLabel = getFilterLabel(filter);

        return {
            filterLabel,
            crm: {
                newEnquiries: periodLeads.length,
                converted: periodLeads.filter((lead) => lead.extra === "Converted").length,
                inProgress: activeLeads.filter((lead) => lead.extra === "In Progress").length,
                pending: activeLeads.filter((lead) => lead.extra === "Pending").length,
                notContacted: activeLeads.filter((lead) => lead.mark === "New").length,
                remindersDueToday,
            },
            orders: {
                total: periodOrders.length,
                delivered: deliveredOrders.length,
                pending: pendingOrders.length,
                cancelled: cancelledOrders.length,
                revenue: periodRevenue,
            },
            inventory: {
                totalProducts: products.length,
                addedInPeriod: periodProducts.length,
                outOfStock,
                lowStock,
            },
        };
    }, [filter, leads, orders, products]);

    if (loading) {
        return (
            <div className="rounded-xl border border-line bg-surface px-4 py-5 text-sm text-ink-muted animate-pulse">
                Loading today&apos;s business summary...
            </div>
        );
    }

    return (
        <section className="rounded-2xl border border-line bg-surface p-3 sm:p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-base sm:text-lg font-bold text-ink">Daily Business Snapshot</h2>
                    <p className="text-xs sm:text-sm text-ink-muted">
                        Quick pulse for CRM follow-ups, order movement, and inventory health — {summary.filterLabel}
                    </p>
                </div>
                <SummaryDateFilter value={filter} onChange={setFilter} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <SummaryColumn
                    title="CRM"
                    icon={Users}
                    tone="from-brand-violet/10 to-brand-indigo/10 border-brand-violet/20"
                    scrollTheme="crm"
                    onViewMore={() => navigate("/crm")}
                    viewLabel="Open CRM"
                >
                    <SummaryMetric
                        label="New Enquiries"
                        value={summary.crm.newEnquiries}
                        hint="Created in selected period"
                    />
                    <SummaryMetric
                        label="Converted"
                        value={summary.crm.converted}
                        tone="success"
                        hint="Won in selected period"
                    />
                    <SummaryMetric
                        label="Follow-ups"
                        value={summary.crm.inProgress + summary.crm.pending}
                        tone="warn"
                        hint="In Progress + Pending"
                    />
                    <SummaryMetric
                        label="Not Contacted"
                        value={summary.crm.notContacted}
                        hint="Still marked New"
                    />
                    <SummaryMetric
                        label="Reminders Today"
                        value={summary.crm.remindersDueToday}
                        tone={summary.crm.remindersDueToday > 0 ? "danger" : "default"}
                        hint="Needs action today"
                    />
                </SummaryColumn>

                <SummaryColumn
                    title="Orders"
                    icon={ShoppingCart}
                    tone="from-brand-lime/10 to-emerald-500/10 border-brand-lime/20"
                    scrollTheme="orders"
                    onViewMore={() => navigate("/orders")}
                    viewLabel="View Orders"
                >
                    <SummaryMetric
                        label="Orders"
                        value={summary.orders.total}
                        hint="In selected period"
                    />
                    <SummaryMetric
                        label="Delivered"
                        value={summary.orders.delivered}
                        tone="success"
                    />
                    <SummaryMetric
                        label="In Pipeline"
                        value={summary.orders.pending}
                        tone="warn"
                        hint="Pending to delivery"
                    />
                    <SummaryMetric
                        label="Revenue"
                        value={formatCurrency(summary.orders.revenue)}
                        tone="success"
                        hint="Delivered order value"
                    />
                    <SummaryMetric
                        label="Cancelled"
                        value={summary.orders.cancelled}
                        tone={summary.orders.cancelled > 0 ? "danger" : "default"}
                    />
                </SummaryColumn>

                <SummaryColumn
                    title="Inventory"
                    icon={Package}
                    tone="from-amber-500/10 to-orange-500/10 border-amber-500/20"
                    scrollTheme="inventory"
                    onViewMore={() => navigate("/products")}
                    viewLabel="View Products"
                >
                    <SummaryMetric
                        label="Catalog Size"
                        value={summary.inventory.totalProducts}
                        hint="Active product records"
                    />
                    <SummaryMetric
                        label="Added"
                        value={summary.inventory.addedInPeriod}
                        hint="New in selected period"
                    />
                    <SummaryMetric
                        label="Out of Stock"
                        value={summary.inventory.outOfStock}
                        tone={summary.inventory.outOfStock > 0 ? "danger" : "default"}
                    />
                    <SummaryMetric
                        label="Low Stock"
                        value={summary.inventory.lowStock}
                        tone={summary.inventory.lowStock > 0 ? "warn" : "default"}
                        hint="Less than 10 units"
                    />
                </SummaryColumn>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-ink-secondary">
                <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
                    <Bell size={14} className="text-brand-violet mt-0.5 shrink-0" />
                    <span>
                        {summary.crm.remindersDueToday > 0
                            ? `${summary.crm.remindersDueToday} CRM reminder(s) are due today. Call or message those leads first.`
                            : "No CRM reminders due today. Good time to chase pending enquiries."}
                    </span>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
                    <IndianRupee size={14} className="text-lime-400 mt-0.5 shrink-0" />
                    <span>
                        {summary.orders.pending > 0
                            ? `${summary.orders.pending} order(s) still in pipeline. Check packing and delivery status.`
                            : "No pending orders in this period. Focus on new customer acquisition."}
                    </span>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
                    <Boxes size={14} className="text-amber-400 mt-0.5 shrink-0" />
                    <span>
                        {summary.inventory.outOfStock + summary.inventory.lowStock > 0
                            ? `${summary.inventory.outOfStock} out-of-stock and ${summary.inventory.lowStock} low-stock SKU(s) need replenishment.`
                            : "Inventory looks stable. Monitor fast-moving products before they run low."}
                    </span>
                </div>
            </div>
        </section>
    );
}
